import { useCallback, useEffect, useRef } from "react";

const ACTIVATION_RADIUS = 160;
const SAFE_GAP = 90;
const FOLLOW_SPEED = 22;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function distanceToRect(px, py, rx, ry, rw, rh) {
  const cx = clamp(px, rx, rx + rw);
  const cy = clamp(py, ry, ry + rh);
  return Math.hypot(px - cx, py - cy);
}

function computeTarget(cursorX, cursorY, curX, curY, bw, bh, boundsW, boundsH) {
  const maxX = Math.max(boundsW - bw, 0);
  const maxY = Math.max(boundsH - bh, 0);

  const dist = distanceToRect(cursorX, cursorY, curX, curY, bw, bh);
  if (dist >= ACTIVATION_RADIUS) {
    return { x: curX, y: curY };
  }

  const boxCx = curX + bw / 2;
  const boxCy = curY + bh / 2;

  let dirX = boxCx - cursorX;
  let dirY = boxCy - cursorY;
  let dirLen = Math.hypot(dirX, dirY);

  if (dirLen < 1) {
    const angle = Math.atan2(boundsH / 2 - cursorY, boundsW / 2 - cursorX) + Math.PI;
    dirX = Math.cos(angle);
    dirY = Math.sin(angle);
    dirLen = 1;
  }

  dirX /= dirLen;
  dirY /= dirLen;

  const desiredCenterX = cursorX + dirX * (SAFE_GAP + Math.max(bw, bh) / 2 + 20);
  const desiredCenterY = cursorY + dirY * (SAFE_GAP + Math.max(bw, bh) / 2 + 20);

  let targetX = clamp(desiredCenterX - bw / 2, 0, maxX);
  let targetY = clamp(desiredCenterY - bh / 2, 0, maxY);

  const check = distanceToRect(cursorX, cursorY, targetX, targetY, bw, bh);
  if (check < SAFE_GAP) {
    const corners = [
      { x: 0, y: 0 },
      { x: maxX, y: 0 },
      { x: 0, y: maxY },
      { x: maxX, y: maxY },
    ];
    let best = corners[0];
    let bestDist = -1;
    for (const c of corners) {
      const d = distanceToRect(cursorX, cursorY, c.x, c.y, bw, bh);
      if (d > bestDist) {
        bestDist = d;
        best = c;
      }
    }
    targetX = best.x;
    targetY = best.y;
  }

  return { x: targetX, y: targetY };
}

export function useDodgeButton({ enabled, initialPos = { x: 160, y: 8 } }) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const cursorRef = useRef({ x: -9999, y: -9999 });
  const posRef = useRef({ ...initialPos });
  const targetRef = useRef({ ...initialPos });
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);

  const applyTransform = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const { x, y } = posRef.current;
    button.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }, []);

  const resetMotion = useCallback(() => {
    posRef.current = { ...initialPos };
    targetRef.current = { ...initialPos };
    cursorRef.current = { x: -9999, y: -9999 };
    lastTimeRef.current = 0;
    applyTransform();
  }, [initialPos, applyTransform]);

  const tick = useCallback(
    (timestamp) => {
      const container = containerRef.current;
      const button = buttonRef.current;
      if (!container || !button) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const lastTime = lastTimeRef.current || timestamp;
      const dt = Math.min((timestamp - lastTime) / 1000, 0.05);
      lastTimeRef.current = timestamp;

      const rect = container.getBoundingClientRect();
      const bw = button.offsetWidth;
      const bh = button.offsetHeight;
      const mx = cursorRef.current.x - rect.left;
      const my = cursorRef.current.y - rect.top;

      targetRef.current = computeTarget(
        mx,
        my,
        posRef.current.x,
        posRef.current.y,
        bw,
        bh,
        rect.width,
        rect.height
      );

      const t = 1 - Math.exp(-FOLLOW_SPEED * dt);
      posRef.current.x += (targetRef.current.x - posRef.current.x) * t;
      posRef.current.y += (targetRef.current.y - posRef.current.y) * t;

      applyTransform();
      rafRef.current = requestAnimationFrame(tick);
    },
    [applyTransform]
  );

  const fleeFromPointer = useCallback((clientX, clientY) => {
    cursorRef.current = { x: clientX, y: clientY };
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      fleeFromPointer(event.clientX, event.clientY);
    },
    [fleeFromPointer]
  );

  useEffect(() => {
    if (!enabled) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }

    resetMotion();
    rafRef.current = requestAnimationFrame(tick);

    const onMove = (event) => fleeFromPointer(event.clientX, event.clientY);
    window.addEventListener("pointermove", onMove, { passive: true });

    return () => {
      window.removeEventListener("pointermove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, fleeFromPointer, resetMotion, tick]);

  return {
    containerRef,
    buttonRef,
    handlePointerMove,
  };
}
